import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import AppLayout from '../../components/AppLayout';
import { useAppAuth } from '@/hooks/useAppAuth';

const quickActions = [
  {
    id: 'find-patient',
    label: '환자 찾기',
    icon: 'ri-search-line',
    color: '#1976D2',
    bg: '#E3F2FD',
    path: '/app/caregiver/search',
  },
  {
    id: 'profile',
    label: '프로필 관리',
    icon: 'ri-user-settings-line',
    color: '#2E7D6B',
    bg: '#E8F5E9',
    path: '/app/profile/edit',
  },
  {
    id: 'schedule',
    label: '스케줄 관리',
    icon: 'ri-calendar-line',
    color: '#F57C00',
    bg: '#FFF3E0',
    path: '/app/caregiver/schedule',
  },
  {
    id: 'history',
    label: '간병 이력',
    icon: 'ri-history-line',
    color: '#7B1FA2',
    bg: '#F3E5F5',
    path: '/app/caregiver/history',
  },
];

export default function AppCaregiverHomePage() {
  const navigate = useNavigate();
  const auth = useAppAuth(true);
  const [profile, setProfile] = useState<{ name: string } | null>(null);
  const [cgProfile, setCgProfile] = useState<{
    is_active: boolean;
    experience_years: number;
    hourly_rate: number;
    total_matches: number;
    rating_avg: number;
    introduction: string;
    available_regions: string[];
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [matchCounts, setMatchCounts] = useState({ requested: 0, ongoing: 0, completed: 0 });
  const [toggleWarning, setToggleWarning] = useState('');

  useEffect(() => {
    if (!auth.userId) return;
    setLoading(true);
    async function load() {
      const { data: p } = await supabase.from('profiles').select('name').eq('id', auth.userId).maybeSingle();
      if (p) setProfile(p);
      const { data: cg } = await supabase
        .from('caregiver_profiles')
        .select('is_active, experience_years, hourly_rate, total_matches, rating_avg, introduction, available_regions')
        .eq('user_id', auth.userId)
        .maybeSingle();
      if (cg) setCgProfile(cg);

      // 매칭 카운트 로드
      try {
        const { data: matches } = await supabase
          .from('match_requests')
          .select('status, requester_id, receiver_id')
          .or(`requester_id.eq.${auth.userId},receiver_id.eq.${auth.userId}`);
        let requested = 0, ongoing = 0, completed = 0;
        (matches || []).forEach((m) => {
          if (m.status === 'pending') requested++;
          else if (m.status === 'accepted') ongoing++;
          else if (m.status === 'completed') completed++;
        });
        setMatchCounts({ requested, ongoing, completed });
      } catch (err) {
        console.error('Failed to load match counts:', err);
      }

      setLoading(false);
    }
    load();
  }, [auth.userId]);

  async function handleToggleActive() {
    if (!auth.userId || !cgProfile) return;

    // 활동 ON 으로 변경하려는 경우 → 예약된 스케줄 있는지 확인
    if (!cgProfile.is_active) {
      setToggling(true);
      setToggleWarning('');
      try {
        const todayStr = new Date().toISOString().slice(0, 10);
        const { data: bookedSchedules } = await supabase
          .from('caregiver_schedules')
          .select('schedule_date')
          .eq('caregiver_user_id', auth.userId)
          .eq('status', 'booked')
          .gte('schedule_date', todayStr)
          .limit(1);

        if (bookedSchedules && bookedSchedules.length > 0) {
          setToggleWarning(`예약된 간병 일정이 있어 활동을 ON으로 변경할 수 없습니다. (${bookedSchedules[0].schedule_date}부터 예약됨) 간병 완료 후 다시 시도해주세요.`);
          setToggling(false);
          return;
        }
      } catch (err) {
        console.error('Failed to check booked schedules:', err);
      }
    }

    setToggling(true);
    const newState = !cgProfile.is_active;
    try {
      const { error } = await supabase
        .from('caregiver_profiles')
        .update({ is_active: newState, updated_at: new Date().toISOString() })
        .eq('user_id', auth.userId);

      if (error) throw error;
      setCgProfile({ ...cgProfile, is_active: newState });
      setToggleWarning('');
    } catch (err) {
      console.error('Failed to toggle active state:', err);
    } finally {
      setToggling(false);
    }
  }

  if (auth.loading || loading) {
    return (
      <AppLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-[#1976D2] border-t-transparent rounded-full animate-spin" />
        </div>
      </AppLayout>
    );
  }

  const profileSteps = [
    { label: '기본 정보', done: !!profile?.name },
    { label: '간병인 상세 정보', done: !!cgProfile },
    { label: '활동 지역 설정', done: (cgProfile?.available_regions?.length || 0) > 0 },
    { label: '자기소개 작성', done: (cgProfile?.introduction?.length || 0) > 10 },
  ];
  const completedSteps = profileSteps.filter((s) => s.done).length;
  const completionPercent = Math.round((completedSteps / profileSteps.length) * 100);

  return (
    <AppLayout>
      <div className="min-h-screen px-5 pt-6 pb-24 bg-gray-50">
        {/* 헤더 */}
        <div className="mb-5">
          <h1 className="text-[22px] font-extrabold text-gray-900 leading-tight">
            {profile ? `${profile.name}님,` : '안녕하세요!'}
            <br />
            <span className="text-[#1976D2]">좋은 매칭</span>이
            <br />
            기다리고 있어요
          </h1>
          <p className="text-sm text-gray-400 mt-1.5">프로필을 완성하고 일자리를 찾아보세요</p>
        </div>

        {/* 활동 ON/OFF 토글 */}
        {cgProfile && (
          <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${cgProfile.is_active ? 'bg-[#E8F5E9]' : 'bg-gray-100'}`}>
                  <i className={`${cgProfile.is_active ? 'ri-check-line text-[#2E7D6B]' : 'ri-pause-line text-gray-400'} text-lg`} />
                </div>
                <div>
                  <p className="text-sm font-extrabold text-gray-900">
                    {cgProfile.is_active ? '활동 ON' : '활동 OFF'}
                  </p>
                  <p className="text-[11px] text-gray-400">
                    {cgProfile.is_active ? '환자에게 프로필이 노출됩니다' : '새로운 매칭을 받지 않습니다'}
                  </p>
                </div>
              </div>
              <button
                onClick={handleToggleActive}
                disabled={toggling}
                className={`relative w-14 h-8 rounded-full transition-colors cursor-pointer disabled:opacity-50 ${
                  cgProfile.is_active ? 'bg-[#2E7D6B]' : 'bg-gray-300'
                }`}
              >
                <div
                  className={`absolute top-1 w-6 h-6 rounded-full bg-white shadow-sm transition-transform ${
                    cgProfile.is_active ? 'translate-x-7' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            {toggleWarning && (
              <div className="mt-3 p-3 rounded-xl bg-red-50 border border-red-100">
                <p className="text-xs font-bold text-red-500 flex items-start gap-1.5">
                  <i className="ri-error-warning-line shrink-0 mt-0.5" />
                  <span>{toggleWarning}</span>
                </p>
              </div>
            )}
          </div>
        )}

        {/* 빠른 액션 버튼 그리드 (2x2) */}
        <div className="grid grid-cols-2 gap-3 mb-5">
          {quickActions.map((action) => (
            <button
              key={action.id}
              onClick={() => navigate(action.path)}
              className="bg-white rounded-2xl border border-gray-100 p-4 text-left cursor-pointer hover:shadow-sm transition-all active:scale-[0.98]"
            >
              <div
                className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
                style={{ backgroundColor: action.bg }}
              >
                <i className={`${action.icon} text-lg`} style={{ color: action.color }} />
              </div>
              <h3 className="text-sm font-bold text-gray-900">{action.label}</h3>
            </button>
          ))}
        </div>

        {/* 프로필 상태 카드 */}
        <div className="rounded-2xl p-5 mb-5 text-white" style={{ backgroundColor: '#1976D2' }}>
          <div className="flex items-start justify-between mb-3">
            <div>
              <p className="text-xs text-white/70 mb-1">내 프로필 상태</p>
              <p className="text-lg font-extrabold">
                {cgProfile?.is_active ? '간병 가능' : '현재 간병중'}
              </p>
            </div>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${cgProfile?.is_active ? 'bg-green-400' : 'bg-amber-400'}`}>
              <i className={`${cgProfile?.is_active ? 'ri-check-line' : 'ri-heart-pulse-line'} text-lg`} />
            </div>
          </div>

          {/* 프로필 완성도 */}
          <div className="mb-3">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-xs text-white/80">프로필 완성도</span>
              <span className="text-xs font-bold text-white">{completionPercent}%</span>
            </div>
            <div className="h-2 rounded-full bg-white/20 overflow-hidden">
              <div
                className="h-full rounded-full bg-white transition-all duration-500"
                style={{ width: `${completionPercent}%` }}
              />
            </div>
          </div>

          <div className="flex flex-wrap gap-1.5 mb-4">
            {profileSteps.map((s) => (
              <span
                key={s.label}
                className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${
                  s.done ? 'bg-white/20 text-white' : 'bg-white/10 text-white/50'
                }`}
              >
                {s.done ? '✓' : '○'} {s.label}
              </span>
            ))}
          </div>

          <button
            onClick={() => navigate('/app/profile/edit')}
            className="w-full py-2.5 rounded-xl bg-white text-sm font-bold cursor-pointer hover:opacity-90 transition-opacity"
            style={{ color: '#1976D2' }}
          >
            프로필 {cgProfile ? '수정하기' : '등록하기'}
          </button>
        </div>

        {/* 통계 카드 */}
        {cgProfile && (
          <div className="grid grid-cols-2 gap-3 mb-5">
            <div className="bg-white rounded-2xl border border-gray-100 p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-[#E3F2FD] flex items-center justify-center">
                  <i className="ri-user-follow-line text-sm text-[#1976D2]" />
                </div>
              </div>
              <p className="text-xl font-extrabold text-gray-900">{cgProfile.total_matches}회</p>
              <p className="text-[11px] text-gray-400 font-medium">총 매칭</p>
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                  <i className="ri-star-fill text-sm text-amber-400" />
                </div>
              </div>
              <div className="flex items-center gap-1">
                <p className="text-xl font-extrabold text-gray-900">{cgProfile.rating_avg}</p>
                <span className="text-[11px] text-gray-400">/ 5.0</span>
              </div>
              <p className="text-[11px] text-gray-400 font-medium">평점</p>
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-[#E8F5E9] flex items-center justify-center">
                  <i className="ri-money-dollar-circle-line text-sm text-[#2E7D6B]" />
                </div>
              </div>
              <p className="text-xl font-extrabold text-gray-900">{cgProfile.hourly_rate.toLocaleString()}원</p>
              <p className="text-[11px] text-gray-400 font-medium">희망 시급</p>
            </div>
            <div className="bg-white rounded-2xl border border-gray-100 p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center">
                  <i className="ri-briefcase-line text-sm text-[#F57C00]" />
                </div>
              </div>
              <p className="text-xl font-extrabold text-gray-900">{cgProfile.experience_years}년</p>
              <p className="text-[11px] text-gray-400 font-medium">경력</p>
            </div>
          </div>
        )}

        {/* 매칭 현황 */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-extrabold text-gray-900">매칭 요청 현황</h2>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate('/app/caregiver/history')}
                className="text-xs text-[#7B1FA2] font-bold cursor-pointer flex items-center gap-0.5"
              >
                간병이력 <i className="ri-arrow-right-s-line" />
              </button>
              <button
                onClick={() => navigate('/app/matching')}
                className="text-xs text-[#1976D2] font-bold cursor-pointer flex items-center gap-0.5"
              >
                전체보기 <i className="ri-arrow-right-s-line" />
              </button>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="grid grid-cols-3 divide-x divide-gray-50">
              {[
                { label: '새 요청', count: matchCounts.requested, icon: 'ri-notification-3-line', color: '#F57C00', bg: '#FFF3E0' },
                { label: '진행 중', count: matchCounts.ongoing, icon: 'ri-loop-left-line', color: '#1976D2', bg: '#E3F2FD' },
                { label: '완료', count: matchCounts.completed, icon: 'ri-check-double-line', color: '#2E7D6B', bg: '#E8F5E9' },
              ].map((stat) => (
                <div key={stat.label} className="p-4 text-center">
                  <div
                    className="w-8 h-8 rounded-lg flex items-center justify-center mx-auto mb-2"
                    style={{ backgroundColor: stat.bg }}
                  >
                    <i className={`${stat.icon} text-sm`} style={{ color: stat.color }} />
                  </div>
                  <p className="text-lg font-extrabold text-gray-900">{stat.count}</p>
                  <p className="text-[11px] text-gray-400 font-medium">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* 서비스 이용 안내 */}
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <h2 className="text-sm font-extrabold text-gray-900 mb-4 flex items-center gap-2">
            <i className="ri-information-line text-[#1976D2] text-lg" />
            간병인 이용 안내
          </h2>
          <div className="space-y-4">
            {[
              {
                step: '01',
                title: '프로필 완성하고 활성화',
                desc: '경력, 자격증, 활동 지역, 자기소개를 상세히 작성하면 환자분들에게 더 잘 노출됩니다.',
                icon: 'ri-user-star-line',
              },
              {
                step: '02',
                title: '환자 찾기 & 매칭 제안',
                desc: '조건에 맞는 환자를 찾아 매칭을 제안하세요. 환자가 수락하면 연락처가 공개됩니다.',
                icon: 'ri-search-eye-line',
              },
              {
                step: '03',
                title: '간병 완료 후 정산',
                desc: '간병비는 환자와 직접 정산합니다. 간병 완료 후 회사에 중개 수수료를 납부합니다.',
                icon: 'ri-exchange-funds-line',
              },
            ].map((item) => (
              <div key={item.step} className="flex gap-3">
                <div className="w-9 h-9 rounded-xl bg-[#E3F2FD] flex items-center justify-center shrink-0">
                  <i className={`${item.icon} text-sm text-[#1976D2]`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-[10px] font-extrabold text-[#1976D2] bg-[#E3F2FD] px-1.5 py-0.5 rounded-md">
                      {item.step}
                    </span>
                    <h3 className="text-sm font-bold text-gray-900">{item.title}</h3>
                  </div>
                  <p className="text-xs text-gray-400 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}